import React, { useEffect, useRef, useState, useMemo } from 'react';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import Tooltip from './Tooltip';
import ArticleInfoBox from './ArticleInfoBox';
import { Box, IconButton, Typography, Tooltip as DefaultTooltip } from '@mui/material';
import ShuffleIcon from '@mui/icons-material/Shuffle';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import ListIcon from '@mui/icons-material/List';
import { Link } from 'react-router-dom';
import { TourProvider, useTour } from '@reactour/tour';
import localforage from 'localforage';

// Import the texture directly
import earthTexture from '../assets/earth-texture.webp';
import starsTexture from '../assets/stars-texture.webp';

const Globe3D = ({
    width = 600,
    height = 600,
    markers = [],
    onLoadMore,
    isLoading,
    hasReachedLimit
}) => {
    const mountRef = useRef(null);
    const [tooltip, setTooltip] = useState({ text: '', position: null });
    const blinkingMarkerRef = useRef(null);
    const originalColorRef = useRef(new THREE.Color(0xff0000));
    const [selectedArticle, setSelectedArticle] = useState(null);
    const [isTourOpen, setIsTourOpen] = useState(false);
    const { setIsOpen } = useTour();



    useEffect(() => {
        const checkFirstVisit = async () => {
            const hasSeenTour = await localforage.getItem('hasSeenGlobeTour');
            if (!hasSeenTour) {
                setIsTourOpen(true);
                await localforage.setItem('hasSeenGlobeTour', true);
            }
        };
        checkFirstVisit();
    }, []);

    useEffect(() => {
        setIsOpen(isTourOpen);
    }, [isTourOpen, setIsOpen]);


    // Add refs for rotation state
    const earthRotationRef = useRef({ y: 0 });
    const skyboxRotationRef = useRef({ y: 0 });
    const markerGroupRef = useRef(null);
    const cameraRef = useRef(null);

    // Memoize textures
    const textures = useMemo(() => {
        const textureLoader = new THREE.TextureLoader();
        return {
            earth: textureLoader.load(earthTexture),
            stars: textureLoader.load(starsTexture)
        };
    }, []); // Empty dependency array as textures should only be loaded once

    // Define EARTH_RADIUS as a constant
    const EARTH_RADIUS = 8;

    // Convert lat/lng to 3D coordinates
    const latLngToVector3 = (lat, lng, radius) => {
        const phi = (90 - lat) * (Math.PI / 180);
        const theta = (lng + 180) * (Math.PI / 180);
        const x = -(radius * Math.sin(phi) * Math.cos(theta));
        const z = radius * Math.sin(phi) * Math.sin(theta);
        const y = radius * Math.cos(phi);
        return new THREE.Vector3(x, y, z);
    };

    // Create marker mesh
    const createMarker = (position) => {
        const markerGeometry = new THREE.SphereGeometry(0.25, 16, 16);
        const markerMaterial = new THREE.MeshBasicMaterial({ color: 0xff0000 });
        const marker = new THREE.Mesh(markerGeometry, markerMaterial);
        marker.position.copy(position);
        return marker;
    };

    // Update the isMarkerVisible function to handle edge cases better
    const isMarkerVisible = (marker, camera, threshold = Math.PI / 2) => {
        const markerPosition = marker.getWorldPosition(new THREE.Vector3());
        const earthPosition = new THREE.Vector3(0, 0, 0);

        // Vector from camera to marker
        const cameraToMarker = markerPosition.clone().sub(camera.position);
        // Get camera's view direction
        const cameraDirection = new THREE.Vector3(0, 0, -1);
        cameraDirection.applyQuaternion(camera.quaternion);

        // Angle between camera's view direction and direction to marker
        const angle = cameraToMarker.normalize().angleTo(cameraDirection);

        // Additional check for markers near the edges
        const distanceToCenter = markerPosition.clone().sub(earthPosition).length();
        const projectedDistance = Math.abs(Math.cos(angle) * distanceToCenter);

        return angle < threshold && projectedDistance > EARTH_RADIUS * 0.3; // Adjust 0.3 to control edge sensitivity
    };


    // Update the isMarkerNearHorizon function to be more sensitive
    const isMarkerNearHorizon = (marker, camera) => {
        // Use an even smaller threshold (60 degrees) to trigger earlier
        return !isMarkerVisible(marker, camera, Math.PI / 3);
    };

    // Update the selectRandomVisibleMarker function with proper checks
    const selectRandomVisibleMarker = (markers, camera, currentMarker) => {
        if (!markers || !markers.length) {
            console.warn('No markers available');
            return null;
        }

        // Convert to array if markers is array-like object
        const markersArray = Array.from(markers);

        const visibleMarkers = markersArray.filter(marker => {
            if (!marker || !(marker instanceof THREE.Mesh)) {
                return false;
            }
            return isMarkerVisible(marker, camera) && marker !== currentMarker;
        });

        if (!visibleMarkers.length) {
            console.log('No visible markers found');
            return null;
        }

        // Get top 3 most visible markers
        const sortedMarkers = visibleMarkers.map(marker => ({
            marker,
            score: getVisibilityScore(marker, camera)
        })).sort((a, b) => b.score - a.score);

        const topMarkers = sortedMarkers.slice(0, Math.min(3, sortedMarkers.length));
        const randomIndex = Math.floor(Math.random() * topMarkers.length);
        return topMarkers[randomIndex].marker;
    };

    // Add helper function for visibility score
    const getVisibilityScore = (marker, camera) => {
        const markerPosition = marker.getWorldPosition(new THREE.Vector3());
        const cameraToMarker = markerPosition.clone().sub(camera.position);
        const cameraDirection = new THREE.Vector3(0, 0, -1).applyQuaternion(camera.quaternion);
        const angle = cameraToMarker.normalize().angleTo(cameraDirection);
        return 1 - (angle / (Math.PI / 2));
    };

    useEffect(() => {
        // Scene setup
        const scene = new THREE.Scene();
        const camera = new THREE.PerspectiveCamera(75, width / height, 0.1, 1000);
        cameraRef.current = camera; // Store camera reference
        const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
        renderer.setSize(width, height);
        mountRef.current.appendChild(renderer.domElement);

        // Controls
        const controls = new OrbitControls(camera, renderer.domElement);
        controls.enableDamping = true;
        controls.dampingFactor = 0.05;
        controls.rotateSpeed = 0.5;

        // Earth setup with default material
        const geometry = new THREE.SphereGeometry(EARTH_RADIUS, 32, 32);
        const defaultMaterial = new THREE.MeshPhongMaterial({ color: 0x2233ff });
        const earth = new THREE.Mesh(geometry, defaultMaterial);
        scene.add(earth);

        // Add Skybox (stars sphere)
        const skyGeometry = new THREE.SphereGeometry(50, 32, 32);  // Much larger than Earth
        const skyMaterial = new THREE.MeshBasicMaterial({
            map: textures.stars,
            side: THREE.BackSide  // Render the inside of the sphere
        });
        const skybox = new THREE.Mesh(skyGeometry, skyMaterial);
        scene.add(skybox);

        // Add markers
        const markerGroup = new THREE.Group();
        markerGroupRef.current = markerGroup; // Store marker group reference
        markers.forEach(({ lat, lng }) => {
            const position = latLngToVector3(lat, lng, EARTH_RADIUS);
            const marker = createMarker(position);
            markerGroup.add(marker);
        });
        earth.add(markerGroup);

        // Add this inside the useEffect, after creating markers
        markerGroup.children.forEach((marker, index) => {
            const originalScale = marker.scale.clone();

            // Add hover effect
            marker.callback = () => {
                marker.scale.set(1.5, 1.5, 1.5);
                // Optional: Show tooltip with article title
                if (markers[index].title) {
                    console.log(markers[index].title);
                }
            };

            marker.callbackLeave = () => {
                marker.scale.copy(originalScale);
            };
        });

        // Add raycaster for hover detection
        const raycaster = new THREE.Raycaster();
        const mouse = new THREE.Vector2();

        const onMouseMove = (event) => {
            const rect = renderer.domElement.getBoundingClientRect();
            mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
            mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

            raycaster.setFromCamera(mouse, camera);
            const intersects = raycaster.intersectObjects(markerGroup.children, true);

            // Reset all markers to original scale
            markerGroup.children.forEach((marker) => {
                if (marker.callbackLeave) marker.callbackLeave();
            });

            if (intersects.length > 0) {
                const intersectedMarker = intersects[0].object;
                if (intersectedMarker.callback) {
                    intersectedMarker.callback();
                    // Show tooltip
                    const markerIndex = markerGroup.children.indexOf(intersectedMarker);
                    if (markerIndex !== -1 && markers[markerIndex]?.title) {
                        setTooltip({
                            text: markers[markerIndex].title,
                            position: {
                                x: event.clientX - rect.left,
                                y: event.clientY - rect.top
                            }
                        });
                        return; // Exit early after setting tooltip
                    }
                }
            }

            // Hide tooltip when not hovering over marker
            setTooltip({ text: '', position: null });
        };

        // Add onClick handler inside useEffect after onMouseMove definition
        const onClick = (event) => {
            const rect = renderer.domElement.getBoundingClientRect();
            mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
            mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

            raycaster.setFromCamera(mouse, camera);
            const intersects = raycaster.intersectObjects(markerGroup.children);

            if (intersects.length > 0) {
                const clickedMarker = intersects[0].object;
                // Reset current blinking marker if exists
                if (blinkingMarkerRef.current) {
                    blinkingMarkerRef.current.material.color = originalColorRef.current;
                }
                // Set clicked marker as new blinking marker
                blinkingMarkerRef.current = clickedMarker;
                // Update selected article
                const markerIndex = markerGroup.children.indexOf(clickedMarker);
                if (markerIndex !== -1) {
                    setSelectedArticle(markers[markerIndex]);
                }
            }
        };

        // Add event listener after mousemove listener
        renderer.domElement.addEventListener('click', onClick);

        renderer.domElement.addEventListener('mousemove', onMouseMove);

        // Lighting
        const ambientLight = new THREE.AmbientLight(0xffffff, 0.8);
        scene.add(ambientLight);

        const pointLight = new THREE.PointLight(0xffffff, 1);
        pointLight.position.set(15, 15, 15);
        scene.add(pointLight);

        // Adjust camera position to accommodate larger globe
        camera.position.z = 17; // Increase from 12 to 20

        // Use memoized textures
        const earthMaterial = new THREE.MeshPhongMaterial({
            map: textures.earth,
            bumpScale: 0.1,
        });
        earth.material = earthMaterial;

        // Add initial marker selection after markers are created
        const selectInitialMarker = () => {
            const initialMarker = selectRandomVisibleMarker(
                markerGroup.children,
                camera,
                null
            );
            if (initialMarker) {
                blinkingMarkerRef.current = initialMarker;
                const markerIndex = markerGroup.children.indexOf(initialMarker);
                if (markerIndex !== -1) {
                    setSelectedArticle(markers[markerIndex]);
                }
            }
        };

        // Call after scene setup is complete
        selectInitialMarker();

        const animate = () => {
            requestAnimationFrame(animate);

            // Update and store rotation
            earthRotationRef.current.y += 0.0003; // Reduced from 0.001
            skyboxRotationRef.current.y -= 0.0001; // Reduced from 0.0002


            earth.rotation.y = earthRotationRef.current.y;
            skybox.rotation.y = skyboxRotationRef.current.y;

            // Force marker change every 60 seconds
            const currentTime = Date.now() * 0.001;
            const shouldChangeMarker = Math.floor(currentTime) % 60 === 0;

            if (blinkingMarkerRef.current) {
                const intensity = (Math.sin(currentTime * 3) + 1) / 2;
                const blinkColor = new THREE.Color(0xffffff).lerp(originalColorRef.current, intensity);
                blinkingMarkerRef.current.material.color = blinkColor;

                // Find the article data for the blinking marker
                const markerIndex = markerGroup.children.indexOf(blinkingMarkerRef.current);
                if (markerIndex !== -1 && markers[markerIndex] !== selectedArticle) {
                    setSelectedArticle(markers[markerIndex]);
                }

                if (shouldChangeMarker || isMarkerNearHorizon(blinkingMarkerRef.current, camera)) {
                    const newMarker = selectRandomVisibleMarker(markerGroup, camera, blinkingMarkerRef.current);
                    if (newMarker) {
                        blinkingMarkerRef.current.material.color = originalColorRef.current;
                        blinkingMarkerRef.current = newMarker;
                    }
                }
            } else {
                const newMarker = selectRandomVisibleMarker(markerGroup, camera, null);
                if (newMarker) {
                    blinkingMarkerRef.current = newMarker;
                }
            }

            renderer.render(scene, camera);
        };

        // Set initial rotation from stored value
        earth.rotation.y = earthRotationRef.current.y;
        skybox.rotation.y = skyboxRotationRef.current.y;

        animate();

        // Cleanup
        return () => {
            // Store final rotation values before cleanup
            earthRotationRef.current.y = earth.rotation.y;
            skyboxRotationRef.current.y = skybox.rotation.y;

            // Reset blinking marker color if it exists
            if (blinkingMarkerRef.current) {
                blinkingMarkerRef.current.material.color = originalColorRef.current;
            }

            // Remove event listener
            renderer.domElement.removeEventListener('mousemove', onMouseMove);
            renderer.domElement.removeEventListener('click', onClick);

            // Safely remove renderer DOM element
            if (mountRef.current && mountRef.current.contains(renderer.domElement)) {
                mountRef.current.removeChild(renderer.domElement);
            }

            // Dispose of Three.js resources
            renderer.dispose();
            geometry.dispose();
            defaultMaterial.dispose();
            markerGroup.children.forEach(marker => {
                marker.geometry.dispose();
                marker.material.dispose();
            });
            skyGeometry.dispose();
            // Don't dispose of textures here as they're managed by useMemo
        };
    }, [width, height, markers, textures]); // Add textures to dependencies

    // Update handleRandomMarker with additional checks
    const handleRandomMarker = () => {
        if (!markerGroupRef.current?.children || !cameraRef.current) {
            console.warn('Required references not available');
            return;
        }

        if (blinkingMarkerRef.current) {
            blinkingMarkerRef.current.material.color = originalColorRef.current;
        }

        const newMarker = selectRandomVisibleMarker(
            Array.from(markerGroupRef.current.children), // Convert to array
            cameraRef.current,
            blinkingMarkerRef.current
        );

        if (newMarker) {
            blinkingMarkerRef.current = newMarker;
            const markerIndex = markerGroupRef.current.children.indexOf(newMarker);
            if (markerIndex !== -1) {
                setSelectedArticle(markers[markerIndex]);
            }
        }
    };

    // Display count with 99+ limit
    const displayCount = markers.length > 99 ? '99+' : markers.length.toString();


    // Update the return statement
    return (
        <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            position: 'relative'
        }}>
            <Box sx={{ position: 'relative', width, height }}>
                <div ref={mountRef} style={{ width, height }} />
                {/* Random marker button */}
                <Tooltip
                    text={tooltip.text}
                    position={tooltip.position}
                    containerWidth={width}
                    containerHeight={height}
                /><DefaultTooltip title="Select random marker" arrow placement="top">
                    <IconButton
                        onClick={handleRandomMarker}
                        data-tour="random-article"
                        sx={{
                            position: 'absolute',
                            top: 10,
                            right: 10,
                            backgroundColor: 'rgba(255, 255, 255, 0.1)',
                            '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.2)' },
                            color: 'white'
                        }}
                    >

                        <ShuffleIcon />
                    </IconButton>
                </DefaultTooltip>
                {/* Article count badge */}
                <Box
                    data-tour="article-count"
                    sx={{
                        position: 'absolute',
                        top: 10,
                        right: 110,
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.2)' },
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontSize: '0.875rem'
                    }}
                >
                    <Typography variant="body2">
                        {displayCount}
                    </Typography>
                </Box>
                {/* Load more button */}
                <DefaultTooltip title="Load more articles" arrow placement="top">
                    <IconButton
                        onClick={onLoadMore}
                        disabled={hasReachedLimit} //removed isLoading ||
                        data-tour="load-more"
                        sx={{
                            position: 'absolute',
                            top: 10,
                            right: 60,
                            backgroundColor: 'rgba(255, 255, 255, 0.1)',
                            '&:hover': {
                                backgroundColor: hasReachedLimit ? 'rgba(128, 128, 128, 0.2)' : 'rgba(76, 175, 80, 0.2)'
                            },
                            color: hasReachedLimit ? 'grey.500' : 'white'
                        }}
                    >
                        <AddCircleIcon />
                    </IconButton>
                </DefaultTooltip>
                <DefaultTooltip title="Go to list view" arrow placement="top">
                    <IconButton
                        component={Link}
                        to="/articles"
                        data-tour="list-view"
                        sx={{
                            position: 'absolute',
                            top: 10,
                            right: 160,
                            color: 'white',
                            backgroundColor: 'rgba(255, 255, 255, 0.1)',
                            '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.2)' },
                        }}
                    >
                        <ListIcon />
                    </IconButton>
                </DefaultTooltip>
            </Box>
            <Box data-tour="article-info"
                sx={{
                    width: width,
                    position: 'relative',
                    minHeight: '150px' // Match ArticleInfoBox minHeight
                }}>
                <ArticleInfoBox article={selectedArticle} />
            </Box>
        </Box>
    );
};

const steps = [
    {
        selector: '[data-tour="list-view"]',
        content: 'Switch to list view to see all articles in a table format'
    },
    {
        selector: '[data-tour="article-count"]',
        content: 'See how many climate articles are currently loaded'
    },
    {
        selector: '[data-tour="load-more"]',
        content: 'Load more articles to see more markers on the globe'
    },
    {
        selector: '[data-tour="random-article"]',
        content: 'Click to randomly select another article from visible markers'
    },
    {
        selector: '[data-tour="article-info"]',
        content: 'View article details here. Click the lightbulb to mark as insightful or the link icon to read the full article'
    }
];

// Wrap the export with TourProvider
export default function GlobeWithTour(props) {
    return (
        <TourProvider
            steps={steps}
            styles={{
                popover: (base) => ({
                    ...base,
                    backgroundColor: '#202020',
                    color: 'white',
                    width: '90%',
                    padding: '15px 10px',
                    margin: '0 auto',
                }),
                badge: (base) => ({
                    ...base,
                    backgroundColor: '#FFA500'
                }),
                dot: (base, { current }) => ({
                    ...base,
                    backgroundColor: current ? '#FFA500' : '#666'
                }),
                close: (base) => ({
                    ...base,
                    position: 'absolute',
                    top: '8px',
                    right: '8px',
                    width: '9px',
                    height: '9px',
                    color: '#fff',
                    '&:hover': {
                        color: '#FFA500'
                    }
                })
            }}
        >
            <Globe3D {...props} />
        </TourProvider>
    );
}